import {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useDataProvider, useRecordContext } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { v4 as uuidv4 } from 'uuid';
import { useFormContext, useWatch } from 'react-hook-form';

import { getInlineAsset } from './const';
import { FullEditorialAsset } from 'utility/types/dataProvider';
import { AssetFormValuesType } from 'screens/editorial/asset/context/AssetConfigUtilities';
import { useNxtImport } from 'components/ExportImport/NxtImport/NxtImportContext';

interface EditorialInlineAsset extends FullEditorialAsset {
  placeholder?: string;
  width?: string;
  presentation?: string;
  alignment?: string;
  app?: string;
}

// Interface for override values
export interface OverrideValues {
  'override-title'?: string;
  'override-byline'?: string;
  'override-content'?: string;
}

// Interface for context
export interface InlineAssetContextType {
  nodeID: string;
  inlineProps: NodeViewProps;
  inlineRecord: EditorialInlineAsset | null;
  setInlineRecord: React.Dispatch<React.SetStateAction<EditorialInlineAsset>>;
  view: 'display' | 'edit' | 'properties' | null;
  setView: React.Dispatch<React.SetStateAction<'display' | 'edit' | 'properties' | null>>;
  assetConfig: ReturnType<typeof getInlineAsset> | undefined;
  setAssetConfig: React.Dispatch<
    React.SetStateAction<ReturnType<typeof getInlineAsset> | undefined>
  >;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  refetch: () => void;
  isFetching: boolean;
  isFetchingURL: boolean;
  setIsFetchingURL: React.Dispatch<React.SetStateAction<boolean>>;
  removeAsset: () => void;
  createFileAsset: (file: File) => Promise<void>;
  relContentStyles: any[];
  setRelContentStyles: (styles: any[]) => void;
  isRelatedContent: boolean;
  isShowButtons: boolean;
  setIsShowButtons: React.Dispatch<React.SetStateAction<boolean>>;
  overrideValues: OverrideValues;
  updateOverrideValue: (key: keyof OverrideValues, value: string | undefined) => void;
}

// Context creation
export const InlineAssetContext = createContext<InlineAssetContextType | undefined>(
  undefined,
);

export const useInlineAsset = () => {
  const context = useContext(InlineAssetContext);
  if (!context) {
    throw new Error('useInlineAsset must be used within a InlineAssetContextProvider');
  }
  return context;
};

// Props for context provider
interface InlineAssetContextProviderProps {
  children: React.ReactNode;
  inlineProps: InlineAssetContextType['inlineProps'];
}

export const InlineAssetContextProvider = ({
  children,
  inlineProps,
}: InlineAssetContextProviderProps) => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const nodeID = 'bloxInline_' + inlineProps.getPos();
  const {
    type,
    uuid,
    placeholder,
    importId,
    'override-title': overrideTitle,
    'override-byline': overrideByline,
    'override-content': overrideContent,
  } = inlineProps.node.attrs;

  // Memoized override values object
  const overrideValues: OverrideValues = useMemo(
    () => ({
      'override-title': overrideTitle,
      'override-byline': overrideByline,
      'override-content': overrideContent,
    }),
    [overrideTitle, overrideByline, overrideContent],
  );

  // Function to update override values in node attributes
  const updateOverrideValue = useCallback(
    (key: keyof OverrideValues, value: string | undefined) => {
      inlineProps.updateAttributes({
        [key]: value,
      });
    },
    [inlineProps],
  );
  const record = useRecordContext<{ data?: FullEditorialAsset }>()?.data;
  // If this is a fresh placeholder node (uuid is null and placeholder is true), always start with a blank inlineRecord
  const [inlineRecord, setInlineRecord] = useState<EditorialInlineAsset>(
    placeholder && !uuid ? { type, placeholder: 'true' } : { type },
  );
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [view, setView] = useState<'display' | 'edit' | 'properties' | null>(null);
  const [assetConfig, setAssetConfig] = useState<
    ReturnType<typeof getInlineAsset> | undefined
  >(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingURL, setIsFetchingURL] = useState(false);
  const [relContentStyles, setRelContentStyles] = useState<any[]>([]);
  const [matchFound, setMatchFound] = useState<boolean>(false);
  const isRelatedContent = type === 'relcontent';
  const isTwitter = type === 'twitter';
  const [isShowButtons, setIsShowButtons] = useState(false);
  const [currentObjectURL, setCurrentObjectURL] = useState<string | null>(null);
  const { addImport, imports, cancelImport } = useNxtImport();
  // Ref to break the cancelImport → removeAsset → contextValue dependency chain.
  // cancelImport changes identity on every NxtImport `imports` state update (including
  // progress ticks from other uploads), which would otherwise destabilize contextValue
  // and cause InlineAssetEdit to re-render — killing open file dialogs.
  const cancelImportRef = useRef(cancelImport);
  cancelImportRef.current = cancelImport;

  // Derive only this node's import status to avoid re-running effects on
  // unrelated import progress ticks (including cross-tab localStorage syncs).
  const thisImportStatus = importId ? imports[importId] : undefined;
  const { control } = useFormContext<AssetFormValuesType>();
  const formRelatedContent = useWatch({
    control: control,
    name: 'related_content',
  });

  const { data, error, isLoading, refetch, isSuccess } = useQuery({
    queryKey: ['inlineAsset', uuid],
    queryFn: () => dataProvider.getOne('editorial', { id: uuid }),
    enabled: false,
  });

  const createFileAsset = useCallback(
    async (file: File) => {
      // Validate file parameter
      if (!file) {
        return;
      }

      const importId = uuidv4();
      addImport(importId, 'editorial', file, true);
      inlineProps.updateAttributes({
        placeholder: false,
        importId: importId,
      });

      // Create object URL only for the specific type needed
      const objectURL = URL.createObjectURL(file);
      setCurrentObjectURL(objectURL);

      setInlineRecord((prev) => ({
        ...prev,
        title: file.name,
        files: {
          audio: {
            url: type === 'audio' ? objectURL : undefined,
          },
          video: {
            url: type === 'video' ? objectURL : undefined,
          },
          image: {
            url: type === 'image' ? objectURL : undefined,
          },
        },
      }));
    },
    [addImport, inlineProps, type],
  );

  const removeAsset = useCallback(() => {
    inlineProps.deleteNode();
    if (importId) {
      cancelImportRef.current(importId);
    }
  }, [inlineProps, importId]);

  useEffect(() => {
    if (isSuccess && data?.data) {
      setIsFetching(false);
      setInlineRecord(data.data.data);
      const newConfig = getInlineAsset(data.data.data.type);
      setAssetConfig(newConfig);
    }
  }, [isSuccess, data, uuid]);

  useEffect(() => {
    if (importId && !uuid) {
      if (thisImportStatus) {
        // This browser owns the import — show uploading state.
        setIsUploading(true);
        if (thisImportStatus.isReady && thisImportStatus.data) {
          const importData = thisImportStatus.data;
          const resolvedType = importData.type || type;
          setIsUploading(false);
          setInlineRecord(importData);
          // Set assetConfig immediately so display renders without waiting
          // for a refetch after the key-driven remount.
          const newConfig = getInlineAsset(resolvedType);
          if (newConfig) {
            setAssetConfig(newConfig);
          }
          inlineProps.updateAttributes({
            type: 'asset',
            app: 'editorial',
            uuid: importData.uuid,
            placeholder: false,
            importId: null,
          });
        } else if (thisImportStatus.isFailed) {
          setIsUploading(false);
          inlineProps.updateAttributes({
            placeholder: true,
            importId: null,
          });
        }
      }
      // When thisImportStatus is undefined, this import belongs to another
      // browser/user (synced via Yjs collaboration). Don't show uploading
      // state — wait for the uuid to arrive via Yjs when the remote upload
      // completes. The uuid-fetching effect will then load the asset data.
    }
  }, [importId, uuid, thisImportStatus, inlineProps, type]);

  useEffect(() => {
    if (error) {
      setErrorMessage('Error fetching inline asset data.');
      setErrorSnackbarOpen(true);
    }
  }, [error]);

  useEffect(() => {
    if (uuid && !isFetching && !matchFound) {
      setIsFetching(true);

      const findAndSetAsset = (array: any[]) => {
        if (!array?.length || !inlineProps.node.attrs.uuid) return null;

        return array.find((item) => {
          const match =
            item.uuid === inlineProps.node.attrs.uuid ||
            item.id === inlineProps.node.attrs.uuid;
          return match;
        });
      };

      try {
        const foundAsset =
          findAndSetAsset(record?.children || []) ||
          findAndSetAsset(record?.parents || []) ||
          findAndSetAsset(record?.siblings || []) ||
          findAndSetAsset(record?.related_content || []);

        if (foundAsset) {
          const newConfig = getInlineAsset(foundAsset.type);
          setAssetConfig(newConfig);
          if (newConfig?.fullData) {
            if (isRelatedContent || isTwitter) {
              return;
            }
            refetch();
            return;
          }
          if (inlineProps.getPos() !== undefined) {
            setInlineRecord(foundAsset);
            return;
          }
        } else {
          if (isRelatedContent || isTwitter) {
            return;
          }
          refetch();
        }
      } catch (error) {
        setInlineRecord(
          (prev) =>
            ({
              ...prev,
              title: 'Asset not found',
              type: 'removed',
            }) as EditorialInlineAsset,
        );
      } finally {
        setIsFetching(false);
        setMatchFound(true);
      }
    }
  }, [
    uuid,
    record,
    inlineProps,
    refetch,
    assetConfig,
    isFetching,
    matchFound,
    isRelatedContent,
    isTwitter,
  ]);

  useEffect(() => {
    if (placeholder) {
      setView('edit');
      setAssetConfig(getInlineAsset(type));
    } else {
      /**
       *  Twitter is currently the only exception here for displaying properly
       *  as it's the only item that's an inline asset but not an inline asset
       *  It's data is not store in any children/related/sibling/parent lists
       *  so we need to manually set the config for it.
       */
      if (type === 'twitter' || type === 'relcontent') {
        const newConfig = getInlineAsset(type);
        setAssetConfig(newConfig);
      }
      setView('display');
    }
  }, [placeholder, type]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (currentObjectURL) {
        URL.revokeObjectURL(currentObjectURL);
      }
    };
  }, [currentObjectURL]);

  useEffect(() => {
    const newRelatedData = formRelatedContent?.find((item) => {
      return item.id === inlineProps.node.attrs.uuid;
    });
    if (newRelatedData) {
      setInlineRecord(newRelatedData as EditorialInlineAsset);
    }
  }, [formRelatedContent, inlineProps.node.attrs.uuid]);

  const contextValue: InlineAssetContextType = useMemo(
    () => ({
      nodeID,
      inlineProps,
      inlineRecord,
      view,
      setView,
      isLoading,
      refetch,
      assetConfig,
      setAssetConfig,
      removeAsset,
      isFetching,
      createFileAsset,
      isUploading,
      setInlineRecord,
      relContentStyles,
      setRelContentStyles,
      isRelatedContent,
      isShowButtons,
      setIsShowButtons,
      setIsUploading,
      setIsFetchingURL,
      isFetchingURL,
      overrideValues,
      updateOverrideValue,
    }),
    [
      inlineProps,
      inlineRecord,
      view,
      isLoading,
      refetch,
      assetConfig,
      removeAsset,
      isFetching,
      createFileAsset,
      isUploading,
      setInlineRecord,
      relContentStyles,
      setRelContentStyles,
      isRelatedContent,
      nodeID,
      isShowButtons,
      setIsShowButtons,
      setIsUploading,
      isFetchingURL,
      setIsFetchingURL,
      overrideValues,
      updateOverrideValue,
    ],
  );

  return (
    <InlineAssetContext.Provider value={contextValue}>
      <NodeViewWrapper
        data-toc-type={type}
        id={uuid}
        data-view-mode={view}
        contentEditable={false}
      >
        {children}
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={errorSnackbarOpen}
          autoHideDuration={5000}
          onClose={() => {
            setErrorSnackbarOpen(false);
            setErrorMessage('');
          }}
        >
          <Alert
            variant="filled"
            severity="error"
            sx={{ width: '100%' }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </NodeViewWrapper>
    </InlineAssetContext.Provider>
  );
};
